export type LivingWorldHydrologyFeasibilityStatus = "rejected" | "eligible_edge_lab";

export interface LivingWorldHydrologyFeasibilityEvidence {
  schemaVersion: 1;
  buildIdentity: string;
  environmentIdentity: string;
  sourceReference: string;
  actorClassPath: string | null;
  stockClientClassProven: boolean;
  serverRuntimeCallable: boolean;
  editorGateAbsent: boolean;
  boundedSelectorProven: boolean;
  enumeratesWorldWaterActors: boolean;
  replicatedStateProven: boolean;
  collisionUnchanged: boolean;
  navigationUnchanged: boolean;
  exactOwnedActorsProven: boolean;
  automaticRollbackProven: boolean;
  restartCleanupProven: boolean;
}

export interface LivingWorldHydrologyFeasibilityDecision {
  status: LivingWorldHydrologyFeasibilityStatus;
  blockers: string[];
  publicationAuthority: "none";
  mayMutateEdge: boolean;
  mayMutateProduction: false;
}

/**
 * Evaluates evidence for a real water-level mutation. A result can only open a
 * bounded Edge lab; it never grants publication or production authority.
 */
export function assessLivingWorldHydrologyFeasibility(
  evidence: LivingWorldHydrologyFeasibilityEvidence
): LivingWorldHydrologyFeasibilityDecision {
  const blockers: string[] = [];
  if (evidence.schemaVersion !== 1) blockers.push("Unsupported hydrology feasibility evidence schema.");
  if (!evidence.buildIdentity.trim()) blockers.push("Exact game build identity is missing.");
  if (evidence.environmentIdentity !== "4c60a401 / The Nest Development") {
    blockers.push("Hydrology evidence is not pinned to the isolated Edge environment.");
  }
  if (!evidence.sourceReference.trim()) blockers.push("First-hand source evidence is missing.");
  if (!evidence.actorClassPath?.trim()) blockers.push("Exact stock actor class is unproven.");
  if (!evidence.stockClientClassProven) blockers.push("Stock-client class availability is unproven.");
  if (!evidence.serverRuntimeCallable) blockers.push("The route is not callable by the dedicated server at runtime.");
  if (!evidence.editorGateAbsent) blockers.push("The route is editor-gated.");
  if (!evidence.boundedSelectorProven) blockers.push("No bounded actor or regional selector is proven.");
  if (evidence.enumeratesWorldWaterActors) blockers.push("The route enumerates world-wide water actors.");
  if (!evidence.replicatedStateProven) blockers.push("Replicated client-visible water state is unproven.");
  if (!evidence.collisionUnchanged) blockers.push("Water collision can change during the mutation.");
  if (!evidence.navigationUnchanged) blockers.push("AI and player navigation safety is unproven.");
  if (!evidence.exactOwnedActorsProven) blockers.push("Exact actor ownership is unproven.");
  if (!evidence.automaticRollbackProven) blockers.push("Automatic exact rollback is unproven.");
  if (!evidence.restartCleanupProven) blockers.push("Restart cleanup is unproven.");

  return {
    status: blockers.length === 0 ? "eligible_edge_lab" : "rejected",
    blockers,
    publicationAuthority: "none",
    mayMutateEdge: blockers.length === 0,
    mayMutateProduction: false
  };
}

/** Exact-current-build result captured by the offline cooked-bytecode audit. */
export const currentGatewayWaterLevelFeasibility = assessLivingWorldHydrologyFeasibility({
  schemaVersion: 1,
  buildIdentity: "gateway-current-build-2026-08-18",
  environmentIdentity: "4c60a401 / The Nest Development",
  sourceReference: "BP_SurvivalGameMode.UpdateWaterLevel cooked-bytecode audit",
  actorClassPath: null,
  stockClientClassProven: true,
  serverRuntimeCallable: false,
  editorGateAbsent: false,
  boundedSelectorProven: false,
  enumeratesWorldWaterActors: true,
  replicatedStateProven: true,
  collisionUnchanged: false,
  navigationUnchanged: false,
  exactOwnedActorsProven: false,
  automaticRollbackProven: false,
  restartCleanupProven: true
});
